import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import { 
  BarChart3, FileText, ClipboardCheck, Truck, 
  AlertTriangle, GraduationCap, Download, Calendar
} from "lucide-react";

const ReportsPage = () => {
  const { t } = useTranslation(['operations', 'common']);
  const reports = [
    {
      title: "CAPA Summary Report",
      description: "Overview of all corrective and preventive actions with status breakdown",
      icon: AlertTriangle,
      type: "CAPA",
      lastGenerated: "2024-01-20",
      color: "#F87171"
    },
    {
      title: "Audit Performance Report",
      description: "Comprehensive audit scores, findings, and trend analysis",
      icon: ClipboardCheck,
      type: "Audit",
      lastGenerated: "2024-01-18",
      color: "#36A9E1"
    },
    {
      title: "Supplier Qualification Report",
      description: "Supplier status, risk assessment, and qualification metrics",
      icon: Truck,
      type: "Supplier",
      lastGenerated: "2024-01-15",
      color: "#4ADE80"
    },
    {
      title: "Training Compliance Report",
      description: "Department-wise training completion and overdue tracking",
      icon: GraduationCap,
      type: "Training",
      lastGenerated: "2024-01-22",
      color: "#38BDF8"
    },
    {
      title: "Deviation Trend Report",
      description: "Deviation patterns, root cause analysis, and resolution times",
      icon: BarChart3,
      type: "Deviation",
      lastGenerated: "2024-01-19",
      color: "#FBBF24"
    },
    {
      title: "Document Control Report",
      description: "Document versioning, review status, and compliance tracking",
      icon: FileText,
      type: "Document",
      lastGenerated: "2024-01-21",
      color: "#E8A838"
    },
    {
      title: "Management Review Report",
      description: "Executive summary of QMS performance for management review",
      icon: BarChart3,
      type: "Executive",
      lastGenerated: "2024-01-10",
      color: "#36A9E1"
    },
    {
      title: "Regulatory Compliance Report",
      description: "Compliance status against FDA, ANVISA, and other regulations",
      icon: ClipboardCheck,
      type: "Regulatory",
      lastGenerated: "2024-01-17",
      color: "#4ADE80"
    },
    {
      title: "Quality Metrics Dashboard",
      description: "Key quality indicators and performance benchmarks",
      icon: BarChart3,
      type: "Metrics",
      lastGenerated: "2024-01-23",
      color: "#38BDF8"
    }
  ];

  const handleGenerateReport = (report) => {
    alert(`Generating ${report.title}...\n\nThis feature will export the report as PDF/Excel.`);
  };

  return (
    <QMSLayout title={t('reports.title')}>
      {/* Header */}
      <div className="mb-6">
        <p className="text-[#8B95A5]">{t('reports.subtitle')}</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="report-stats">
        <div className="qms-card p-4">
          <p className="text-[#8B95A5] text-xs uppercase">{t('operations:reports.availableReports')}</p>
          <p className="font-['Syne'] text-2xl font-bold text-white">{reports.length}</p>
        </div>
        <div className="qms-card p-4">
          <p className="text-[#8B95A5] text-xs uppercase">{t('operations:reports.lastGenerated')}</p>
          <p className="font-['Syne'] text-2xl font-bold text-white">Today</p>
        </div>
        <div className="qms-card p-4">
          <p className="text-[#8B95A5] text-xs uppercase">{t('operations:reports.scheduled')}</p>
          <p className="font-['Syne'] text-2xl font-bold text-white">5</p>
        </div>
        <div className="qms-card p-4">
          <p className="text-[#8B95A5] text-xs uppercase">{t('operations:reports.formats')}</p>
          <p className="font-['Syne'] text-2xl font-bold text-white">PDF/XLS</p>
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reports-grid">
        {reports.map((report, index) => (
          <div 
            key={index} 
            className="qms-card hover:border-[#2D313D] transition-colors"
            data-testid={`report-card-${index}`}
          >
            <div className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div 
                  className="w-12 h-12 rounded-[3px] flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${report.color}20` }}
                >
                  <report.icon size={24} style={{ color: report.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-['Syne'] text-lg font-semibold text-white mb-1">{report.title}</h3>
                  <span className="badge badge-blue text-xs">{report.type}</span>
                </div>
              </div>
              
              <p className="text-[#8B95A5] text-sm mb-4">{report.description}</p>
              
              <div className="flex items-center justify-between pt-4 border-t border-[rgba(255,255,255,0.07)]">
                <div className="flex items-center gap-2 text-[#8B95A5] text-xs">
                  <Calendar size={14} />
                  <span>Last: {report.lastGenerated}</span>
                </div>
                <button
                  onClick={() => handleGenerateReport(report)}
                  className="flex items-center gap-2 text-[#38BDF8] hover:text-white text-sm transition-colors"
                >
                  <Download size={16} />
                  {t('operations:reports.generate')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Scheduled Reports */}
      <div className="mt-8 qms-card" data-testid="scheduled-reports">
        <div className="qms-card-header">{t('operations:reports.scheduledReports')}</div>
        <div className="qms-card-body">
          <table className="qms-table">
            <thead>
              <tr>
                <th>{t('operations:reports.thReport')}</th>
                <th>{t('operations:reports.thFrequency')}</th>
                <th>{t('operations:reports.thRecipients')}</th>
                <th>{t('operations:reports.thNextRun')}</th>
                <th>{t('operations:reports.thStatus')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-white">Weekly CAPA Summary</td>
                <td>Weekly</td>
                <td>quality@company.com</td>
                <td>2024-01-29</td>
                <td><span className="badge badge-green">Active</span></td>
              </tr>
              <tr>
                <td className="text-white">Monthly Audit Report</td>
                <td>Monthly</td>
                <td>management@company.com</td>
                <td>2024-02-01</td>
                <td><span className="badge badge-green">Active</span></td>
              </tr>
              <tr>
                <td className="text-white">Quarterly Metrics</td>
                <td>Quarterly</td>
                <td>executives@company.com</td>
                <td>2024-04-01</td>
                <td><span className="badge badge-green">Active</span></td>
              </tr>
              <tr>
                <td className="text-white">Training Compliance</td>
                <td>Bi-weekly</td>
                <td>hr@company.com</td>
                <td>2024-01-30</td>
                <td><span className="badge badge-amber">Paused</span></td>
              </tr>
              <tr>
                <td className="text-white">Supplier Review</td>
                <td>Monthly</td>
                <td>procurement@company.com</td>
                <td>2024-02-01</td>
                <td><span className="badge badge-green">Active</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </QMSLayout>
  );
};

export default ReportsPage;
